import { useMemo } from 'react';
import { addWeeks } from 'date-fns';
import { TZDate } from '@date-fns/tz';

import { useZCalendarWebApiHandler_getAvailability } from '@zg-rentals/ts-z-calendar-client-react-query';
import type { TourAvailabilityWebApiDto } from '@zg-rentals/ts-z-calendar-client';

import { convertDateToLocalDate } from '../utils/formatTime';

// Look-ahead window for "upcoming" availability, matching the tour calendar's 3-week range.
const AVAILABILITY_LOOKAHEAD_WEEKS = 3;

type Options = {
  enabled?: boolean;
};

// A slot is upcoming only if its end is still in the future. The end is evaluated in the slot's own
// timezone (via TZDate) rather than the browser's, so a window that already ended today in the
// listing's timezone is excluded even when the viewer is in a different timezone.
const isFutureAvailability = ({ endDateTime, timezone }: TourAvailabilityWebApiDto): boolean => {
  const end = new TZDate(
    endDateTime.date.year,
    endDateTime.date.month - 1,
    endDateTime.date.day,
    endDateTime.time.hour,
    endDateTime.time.minute,
    timezone,
  );
  return end.getTime() > Date.now();
};

/**
 * Returns whether a listing has any open tour availability still in the future, looking at the
 * upcoming weeks. Availability that has already lapsed (e.g. earlier today, in the listing's
 * timezone) does not count.
 */
const useHasFutureTourAvailability = (listingAlias: string, { enabled = true }: Options = {}) => {
  const today = new Date();

  const { data, isError, isLoading, isPending } = useZCalendarWebApiHandler_getAvailability(
    {
      from: convertDateToLocalDate(today),
      to: convertDateToLocalDate(addWeeks(today, AVAILABILITY_LOOKAHEAD_WEEKS)),
      listingAlias,
    },
    { enabled: enabled && Boolean(listingAlias) },
  );

  const hasFutureAvailability = useMemo(() => Boolean(data?.availabilities?.some(isFutureAvailability)), [data]);

  return { hasFutureAvailability, isError, isLoading, isPending };
};

export default useHasFutureTourAvailability;
